import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { downloadFile } from "@/lib/download-file";
import type { Document } from "@/schemas/document";
import { Download, FileText, Loader2 } from "lucide-react";
import { useState } from "react";

interface DocumentPreviewModalProps {
  document: Document | null;
  onOpenChange: (open: boolean) => void;
}

export function DocumentPreviewModal({
  document,
  onOpenChange,
}: DocumentPreviewModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  if (!document) return null;

  const previewUrl = `/api/documents/preview/${document.id}#pagemode=none`;

  return (
    <Dialog open={!!document} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-full h-[90vh] p-0">
        <div className="flex flex-col h-full">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-6 py-2 border-b bg-muted/40 shrink-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold truncate max-w-[300px]">
                {document.name}
              </span>
            </div>
            <div className="flex items-center gap-4 pr-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => downloadFile(document.url, document.name)}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="relative flex-1 bg-muted/10 overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}

              {!iframeError ? (
                document.file_type.startsWith("image/") ? (
                  <div className="w-full h-full overflow-auto p-4">
                    <div className="min-h-full flex items-center justify-center">
                      <img
                        src={previewUrl}
                        alt={document.name}
                        className="max-w-full max-h-[calc(90vh-4rem)] object-contain"
                        onLoad={() => setIsLoading(false)}
                        onError={() => {
                          setIframeError(true);
                          setIsLoading(false);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <object
                    data={previewUrl}
                    type="application/pdf"
                    className="w-full h-full"
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                      setIframeError(true);
                      setIsLoading(false);
                    }}
                  >
                    <embed
                      src={previewUrl}
                      type="application/pdf"
                      className="w-full h-full"
                    />
                  </object>
                )
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <FileText className="h-12 w-12 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    Preview not available for this file type
                  </p>
                  <Button
                    onClick={() => window.open(previewUrl, "_blank")}
                    variant="outline"
                  >
                    Open in New Tab
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
